import React, { useState, useEffect,useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  EyeOff,
  Lock,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Mail,
  Shield,
} from "lucide-react";
import { postData } from "../../services/NodeServices";


const ResetPassword = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);


  const passRef = useRef()

  // Validation states
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [newPasswordError, setNewPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const [passwordChecks, setPasswordChecks] = useState({
    hasNumber: false,
    hasLetter: false,
    hasUpperCase: false,
    hasSpecialChar: false,
    minLength: false,
  });

  const navigate = (path) => {
    window.location.href = path;
  };

  // Show message function
  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 5000);
  };

  // OTP Timer effect
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Email validation
  useEffect(() => {
    if (email && !email.includes("@")) {
      setEmailError("Email Address Must Contain @");
    } else if (email && !email.includes(".")) {
      setEmailError("Email must contain a valid domain");
    } else if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Invalid email format");
    } else {
      setEmailError("");
    }
  }, [email]);

  // OTP validation
  useEffect(() => {
    if (otp && otp.length !== 6) {
      setOtpError("OTP must be 6 digits");
    } else {
      setOtpError("");
    }
  }, [otp]);

  // Password validation
  useEffect(() => {
    const hasNumber = /\d/.test(newPassword);
    const hasLetter = /[a-z]/.test(newPassword);
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    const minLength = newPassword.length >= 8;

    setPasswordChecks({
      hasNumber,
      hasLetter,
      hasUpperCase,
      hasSpecialChar,
      minLength,
    });

    if (newPassword && !minLength) {
      setNewPasswordError("Password must be at least 8 characters");
    } else if (newPassword && !hasNumber) {
      setNewPasswordError("Password must contain at least 1 number");
    } else if (newPassword && !hasLetter) {
      setNewPasswordError("Password must contain at least 1 lowercase letter");
    } else if (newPassword && !hasUpperCase) {
      setNewPasswordError("Password must contain at least 1 uppercase letter");
    } else if (newPassword && !hasSpecialChar) {
      setNewPasswordError("Password must contain at least 1 special character");
    } else {
      setNewPasswordError("");
    }
  }, [newPassword]);

  // Confirm Password validation
  useEffect(() => {
    if (confirmPassword && confirmPassword !== newPassword) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError("");
    }
  }, [confirmPassword, newPassword]);

  const setInputValues = (value, index) => {
    // only allow digits, and max 1 char
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp]; // copy state
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    // auto focus next input if a digit entered
    if (value && index < otp.length - 1) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Step 1: Send OTP to email
  const handleSendOTP = async () => {
    console.log(emailError, email);
    if (emailError || !email) {
      showMessage("Please enter a valid email address", "error");
      return;
    }

    setIsLoading(true);

    try {
      const result = await postData("api/users/send-otp", { email: email });

      if (result.status) {
        showMessage("OTP sent to your email address", "success");
        setCurrentStep(2);
        setOtpTimer(300); // 5 minutes
      } else {
        showMessage(result.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      showMessage("Server error. Please try again later.", "error");
      console.error("Send OTP error:", error);
    }

    setIsLoading(false);
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async () => {
    if (otpError || !otp) {
      showMessage("Please enter a valid 6-digit OTP", "error");
      return;
    }
     let finalotp = otp.join('')
    setIsLoading(true);

    try {
      const result = await postData("api/users/reset-password", {
         email:email,
         otp:finalotp,
         newpass:passRef.current.value
      });

  

      if (result.status) {
        showMessage("OTP verified successfully", "success");
        setCurrentStep(3);
      } else {
        showMessage(result.message || "Invalid OTP", "error");
      }
    } catch (error) {
      showMessage("Server error. Please try again later.", "error");
      console.error("Verify OTP error:", error);
    }

    setIsLoading(false);
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (
      newPasswordError ||
      confirmPasswordError ||
      !newPassword ||
      !confirmPassword
    ) {
      showMessage("Please fix all errors before submitting", "error");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/users/reset-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          otp,
          newPassword,
          confirmPassword,
        }),
      });

      const result = await response.json();

      if (response.ok && result.status) {
        showMessage(
          "Password reset successfully! Redirecting to login...",
          "success"
        );
        setTimeout(() => navigate("/login"), 3000);
      } else {
        showMessage(result.message || "Failed to reset password", "error");
      }
    } catch (error) {
      showMessage("Server error. Please try again later.", "error");
      console.error("Reset password error:", error);
    }

    setIsLoading(false);
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (otpTimer > 0) return;
    await handleSendOTP();
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const isStep1Valid = () => !emailError && email;
  console.log(otpError,otp)
  const isStep2Valid = () => !otpError && otp && otp.length === 6;
  const isStep3Valid = () =>
    !newPasswordError &&
    !confirmPasswordError &&
    newPassword &&
    confirmPassword &&
    Object.values(passwordChecks).every((check) => check);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };


  
const handleKeyDown = (e, index) => {
  if (e.key === "Backspace" && !otp[index] && index > 0) {
    const prevInput = document.getElementById(`otp-input-${index - 1}`);
    if (prevInput) prevInput.focus();
  }
};

  return (
    <div className="flex bg-[#fff] justify-center items-center min-h-screen w-full">
      <div className="flex items-center justify-center p-8 w-full max-w-[575px]">
        <motion.div
          className="w-full max-w-md p-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div
            className="flex items-center gap-3 mb-6"
            variants={itemVariants}
          >
            <motion.button
              onClick={() => {
                if (currentStep === 1) {
                  navigate("/login");
                } else {
                  setCurrentStep(currentStep - 1);
                  setMessage("");
                  setMessageType("");
                }
              }}
              className="p-2 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-100 transition-colors duration-200"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>
            <h1 className="text-2xl font-semibold text-gray-800">
              Reset Password
            </h1>
          </motion.div>

          {/* Progress Indicator */}
          <motion.div
            className="flex items-center mb-8"
            variants={itemVariants}
          >
            <div className="flex items-center w-full">
              {[1, 2, 3].map((step, index) => (
                <React.Fragment key={step}>
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                      step <= currentStep
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {step}
                  </div>
                  {index < 2 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        step < currentStep ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          {/* Step Labels */}
          <motion.div
            className="text-center w-full text-gray-600 mb-6"
            variants={itemVariants}
          >
            <p>
              {currentStep === 1 && "Enter your email address to receive OTP"}
              {currentStep === 2 && "Enter the OTP sent to your email"}
              {currentStep === 3 && "Create a new secure password"}
            </p>
          </motion.div>

          {/* Message Display */}
          <AnimatePresence>
            {message && (
              <motion.div
                className={`mb-6 p-4 rounded-lg ${
                  messageType === "success"
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : "bg-red-100 text-red-800 border border-red-200"
                }`}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                variants={itemVariants}
              >
                {message}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Step Content */}
          <div className="space-y-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Email Input */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div variants={itemVariants}>
                    <label className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="johndoe666@gmail.com"
                        className={`w-full px-8 py-3 pl-11 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 ${
                          emailError
                            ? "border-red-300 focus:ring-red-500"
                            : "border-gray-300 focus:ring-blue-500"
                        }`}
                      />
                      <Mail className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                      {email && (
                        <motion.div
                          className="absolute right-3 top-3.5"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                          }}
                        >
                          {emailError ? (
                            <XCircle className="w-5 h-5 text-red-500" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </motion.div>
                      )}
                    </div>
                    <AnimatePresence>
                      {emailError && (
                        <motion.p
                          className="text-red-500 text-sm mt-2 w-full text-left"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                        >
                          {emailError}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {currentStep == 1 ? (
                    <motion.button
                      onClick={handleSendOTP}
                      disabled={isLoading || !isStep1Valid()}
                      className="w-full bg-[#222] text-white py-3 px-4 rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 mt-6"
                      variants={itemVariants}
                      whileHover={{ scale: isLoading ? 1 : 1.02 }}
                      whileTap={{ scale: isLoading ? 1 : 0.98 }}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Resetting Password...
                        </div>
                      ) : (
                        "Send Otp"
                      )}
                    </motion.button>
                  ) : (
                    <motion.button
                      onClick={handleSendOTP}
                      disabled={isLoading || !isStep3Valid()}
                      className="w-full bg-[#222] text-white py-3 px-4 rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 mt-6"
                      variants={itemVariants}
                      whileHover={{ scale: isLoading ? 1 : 1.02 }}
                      whileTap={{ scale: isLoading ? 1 : 0.98 }}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Resetting Password...
                        </div>
                      ) : (
                        "Reset Password"
                      )}
                    </motion.button>
                  )}
                </motion.div>
              )}

              {currentStep === 2 && (
                <>
                  <div className="flex flex-row justify-center items-center w-full py-4">
                    {otp.map((item, index) => (
                      <input
                        key={index}
                        id={`otp-input-${index}`}
                        className="w-12 h-12 border-[1px] border-solid p-2 border-[#999] bg-[#fff] rounded-md mx-2 text-center text-lg"
                        type="text"
                        maxLength={1}
                        value={item}
                  

                        onChange={(e) => setInputValues(e.target.value, index)}
                      />
                    ))}
                  </div>
                </>
              )}
                  

                  {currentStep==2&&<>
                    <label className="w-full text-left text-sm ">New password</label>

                    <input  className={`w-full border-[1px] border-solid  rounded-md ${newPasswordError?'border-red':'border-[#888]'} px-6 py-3`}
                      ref={passRef}
                    />
                  
                  </>}


                  {currentStep == 2 && (
                    <motion.button
                      onClick={handleVerifyOTP}
                      disabled={isLoading || !isStep2Valid()}
                      className="w-full bg-[#222] text-white py-3 px-4 rounded-full font-medium hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 mt-6"
                      variants={itemVariants}
                      whileHover={{ scale: isLoading ? 1 : 1.02 }}
                      whileTap={{ scale: isLoading ? 1 : 0.98 }}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                         Verifying Otp...
                        </div>
                      ) : (
                        "Verify Otp"
                      )}
                    </motion.button>
                  )
                    
                  }
            </AnimatePresence>

            {/* Back to Login Link */}
            <motion.div
              className="text-center w-full mt-8"
              variants={itemVariants}
            >
              <span className="text-gray-600 text-sm">
                Remember your password?
              </span>
              <motion.a
                href="/log-in"
                className="text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm ml-2"
                whileHover={{ scale: 1.05 }}
              >
                Back to Login
              </motion.a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResetPassword;
